import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, AlertTriangle, CheckCircle2 } from "lucide-react";
import AppShell, { useAppUser, ROLE_LABELS } from "@/components/AppShell";
import BeltBar, { BELT_LABELS, degreeLabel } from "@/components/BeltBar";
import UserAvatar from "@/components/UserAvatar";
import { authApi, formatApiError } from "@/lib/api";
import { imageToDataUrl } from "@/lib/files";

const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "—");
const fmtBirth = (bd) => (bd ? bd.split("-").reverse().join("/") : "—");

const Field = ({ label, value, testid }) => (
  <div className="bg-obsidian border border-white/10 rounded-xl px-4 py-3" data-testid={testid}>
    <p className="font-mono text-[10px] tracking-widest text-zinc-500">{label}</p>
    <p className="text-sm text-[#F5F5F0] mt-1 break-words">{value || "—"}</p>
  </div>
);

export default function MeusDados() {
  const { user } = useAppUser() || {};
  const [me, setMe] = useState(null);
  const [unitName, setUnitName] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    authApi()
      .get("/auth/me")
      .then((r) => {
        setMe(r.data);
        if (r.data.unit_id) {
          authApi()
            .get("/units")
            .then((res) => {
              const u = (res.data.units || []).find((x) => x.id === r.data.unit_id);
              setUnitName(u ? u.name : null);
            })
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, [user]);

  const changePhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setSaving(true);
    try {
      const dataUrl = await imageToDataUrl(file);
      await authApi().patch("/me/photo", { photo: dataUrl });
      setMe((m) => ({ ...m, photo: dataUrl }));
      toast.success("Foto de perfil atualizada.");
    } catch (err) {
      toast.error(err.message || formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const roles = me ? me.roles || [me.role || "aluno"] : [];

  return (
    <AppShell active="aluno">
      <section data-testid="meus-dados-page">
        <h1 className="font-display text-2xl font-bold" data-testid="meus-dados-title">Meus Dados</h1>
        <p className="font-mono text-xs tracking-[0.3em] text-gold mt-2 mb-6">SEU CADASTRO NO PROJETO</p>

        {me === null ? (
          <p className="text-zinc-500 text-sm">Carregando seus dados...</p>
        ) : (
          <div className="space-y-6">
            <div className="bg-[#1C1C23] border border-white/10 rounded-2xl p-6 flex flex-col sm:flex-row items-center gap-6" data-testid="meus-dados-photo-card">
              <UserAvatar name={me.name} photo={me.photo} pendencia={!!me.pendencia} birthDate={me.birth_date} size="lg" testid="meus-dados-avatar" />
              <div className="text-center sm:text-left">
                <p className="font-display font-semibold text-lg" data-testid="meus-dados-name">{me.name}</p>
                <p className="font-mono text-[11px] tracking-wider text-zinc-500 mt-1">
                  {roles.map((r) => (ROLE_LABELS[r] || r).toUpperCase()).join(" + ")}
                </p>
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={saving}
                  data-testid="meus-dados-change-photo-button"
                  className="mt-4 inline-flex items-center gap-2 font-display font-semibold text-sm bg-gold text-obsidian px-5 py-2.5 rounded-full hover:bg-gold-amber disabled:opacity-60 transition-colors"
                >
                  <Camera size={16} /> {saving ? "Enviando..." : "Alterar Foto de Perfil"}
                </button>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={changePhoto} data-testid="meus-dados-photo-input" />
              </div>
              {me.pendencia && (
                <div className="sm:ml-auto flex items-center gap-2 border border-yellow-400/50 bg-yellow-400/10 text-yellow-300 rounded-xl px-4 py-3" data-testid="meus-dados-pendencia-alert">
                  <AlertTriangle size={16} className="shrink-0" />
                  <p className="text-xs">Pendência de boletim escolar. Regularize no menu Pendências.</p>
                </div>
              )}
            </div>

            <div className="bg-[#1C1C23] border border-white/10 rounded-2xl p-6" data-testid="meus-dados-graduation-card">
              <p className="font-mono text-[11px] tracking-widest text-zinc-500 mb-4">MINHA GRADUAÇÃO</p>
              <BeltBar belt={me.belt || "branca"} degree={me.degree || 0} testid="meus-dados-beltbar" />
              <p className="font-display font-semibold mt-3" data-testid="meus-dados-belt-label">
                Faixa {BELT_LABELS[me.belt || "branca"]} · {degreeLabel(me.degree || 0)}
              </p>
              <p className="text-xs text-zinc-500 mt-1" data-testid="meus-dados-last-graduation">
                Última graduação: {fmtDateTime(me.last_graduation_at)}
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="meus-dados-fields">
              <Field label="E-MAIL" value={me.email} testid="meus-dados-email" />
              <Field label="CELULAR" value={me.phone} testid="meus-dados-phone" />
              <Field label="CPF" value={me.cpf} testid="meus-dados-cpf" />
              <Field label="RG" value={me.rg} testid="meus-dados-rg" />
              <Field label="NASCIMENTO" value={`${fmtBirth(me.birth_date)}${me.age != null ? ` · ${me.age} anos` : ""}`} testid="meus-dados-birth" />
              <Field label="PESO" value={me.weight ? `${me.weight} kg` : "—"} testid="meus-dados-weight" />
              <Field label="ALTURA" value={me.height ? `${me.height} cm` : "—"} testid="meus-dados-height" />
              <Field label="UNIDADE" value={unitName} testid="meus-dados-unit" />
              <Field label="ENDEREÇO" value={me.address} testid="meus-dados-address" />
              <Field label="INSTITUIÇÃO DE ACOLHIMENTO" value={me.institution} testid="meus-dados-institution" />
              <Field
                label="SITUAÇÃO"
                value={me.pendencia ? "Com pendência" : "Em dia"}
                testid="meus-dados-status"
              />
            </div>

            {!me.pendencia && (
              <p className="flex items-center gap-2 text-xs text-green-400" data-testid="meus-dados-ok">
                <CheckCircle2 size={14} /> Cadastro sem pendências. Oss!
              </p>
            )}
          </div>
        )}
      </section>
    </AppShell>
  );
}
